import { CancelablePromise } from 'cancelable-promise';
import { getServiceUrl } from '../api.config';
import { ResponseWithBody, ResponseWithData } from '../types';
import { ApiClientOptions } from '../types/api';
import ApiClient from '../utils/ApiClient';

type Role = {
  id: number;
  name: string;
  data: any;
  createdAt: string;
  updatedAt: string;
};

type RolePayload = {
  id?: number;
  name: string;
  data: any;
};

type RolesResponse = Role[];

type Scope = {
  id: number;
  name: string;
  roles?: Role[];
  data: any;
  createdAt: string;
  updatedAt: string;
};

type ScopePayload = {
  id?: number;
  name: string;
  data: any;
};

type ScopesResponse = Scope[];

type LinkPayload = {
  scopes: {
    id: number;
    roles: {
      id: number;
    }[];
  }[];
};

type LinkResponse = {
  id: number;
  scopes: Scope[];
};

type ConfigurationResponse = {
  scopes: object;
  roles: object;
};

type UserListResponse = {
  users_count: number;
  users: {
    username: string;
    id: string | number;
    firstname: string;
    lastname: string;
  }[];
};

export class PermissionApi extends ApiClient {
  public constructor(options: ApiClientOptions = {}) {
    super(`${getServiceUrl('auth')}/auth`, options);
  }

  static V3_PREFIX = '/v3/permissions';

  public getScopes(): CancelablePromise<ResponseWithData<ScopesResponse>> {
    const query = `${PermissionApi.V3_PREFIX}/scope`;
    return this.get(query);
  }

  public createOrUpdateScope(
    scope: ScopePayload,
  ): CancelablePromise<ResponseWithData<Scope>> {
    if (!scope.id) {
      const query = `${PermissionApi.V3_PREFIX}/scope`;
      return this.post(query, scope);
    } else {
      const query = `${PermissionApi.V3_PREFIX}/scope/${scope.id}`;
      return this.put(query, scope);
    }
  }

  public deleteScope(scopeId: number): CancelablePromise<ResponseWithBody> {
    const query = `${PermissionApi.V3_PREFIX}/scope/${scopeId}`;
    return this.delete(query);
  }

  public getRoles(): CancelablePromise<ResponseWithData<RolesResponse>> {
    const query = `${PermissionApi.V3_PREFIX}/role`;
    return this.get(query);
  }

  public getRoleUsers(payload: {
    roleId: string | number;
    offset: number;
    limit: number;
  }): CancelablePromise<ResponseWithData<UserListResponse>> {
    const query = `/v3/permission/role/${payload.roleId}/users?offset=${payload.offset}&limit=${payload.limit}`;
    return this.get(query);
  }

  public getScopeUsers(payload: {
    scopeId: string | number;
    offset: number;
    limit: number;
  }): CancelablePromise<ResponseWithData<UserListResponse>> {
    const query = `/v3/permission/scope/${payload.scopeId}/users?offset=${payload.offset}&limit=${payload.limit}`;
    return this.get(query);
  }

  public createOrUpdateRole(
    role: RolePayload,
  ): CancelablePromise<ResponseWithData<Role>> {
    if (!role.id) {
      const query = `${PermissionApi.V3_PREFIX}/role`;
      return this.post(query, role);
    } else {
      const query = `${PermissionApi.V3_PREFIX}/role/${role.id}`;
      return this.put(query, role);
    }
  }

  public deleteRole(roleId: number): CancelablePromise<ResponseWithBody> {
    const query = `${PermissionApi.V3_PREFIX}/role/${roleId}`;
    return this.delete(query);
  }

  public getLinks(
    userId: number,
  ): CancelablePromise<ResponseWithData<LinkResponse>> {
    const query = `${PermissionApi.V3_PREFIX}/link/${userId}`;
    return this.get(query);
  }

  public createOrReplaceLink(
    linkId: number | string,
    payload: LinkPayload,
  ): CancelablePromise<ResponseWithData<LinkResponse>> {
    const query = `${PermissionApi.V3_PREFIX}/link/${linkId}`;
    return this.post(query, payload);
  }

  public getConfiguration(): CancelablePromise<
    ResponseWithData<ConfigurationResponse>
  > {
    const query = `${PermissionApi.V3_PREFIX}/configuration`;
    return this.get(query);
  }
}
